"use client";

/**
 * @file not-found.tsx
 * @description Página 404 super moderna com motion/react
 */

import { motion } from "motion/react";
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  Home,
  RefreshCw,
  Sparkles,
  Zap,
  AlertTriangle,
} from "lucide-react";
import { Button } from "visuais";
import { Logo } from "@/_nucleo/visuais/layout/logo";

export default function NotFound() {
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Background Particles */}
      <div className="absolute inset-0">
        {isClient &&
          [...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/30 dark:bg-cyan-300/30 rounded-full"
              initial={{
                x:
                  Math.random() *
                  (typeof window !== "undefined" ? window.innerWidth : 1200),
                y:
                  Math.random() *
                  (typeof window !== "undefined" ? window.innerHeight : 800),
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
      </div>

      {/* Gradient Orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.6, 0.3, 0.6],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      {/* Logo Background */}
      <motion.div
        className="absolute -top-128 -left-128 -z-10"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <motion.div
          className="inline-flex items-center justify-center p-4 rounded-2xl"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.05, 1],
          }}
          transition={{
            rotate: { duration: 20, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <Logo className="size-512 opacity-20" color="var(--color-cyan-500)" />
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-4">
        {/* 404 Numbers */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -100, scale: 0.5 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="flex items-center justify-center gap-4 text-[120px] lg:text-[160px] font-black leading-none select-none">
            <motion.span
              className="bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-500 bg-clip-text text-transparent cursor-pointer"
              animate={{
                y: [-5, 5, -5],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{
                scale: 1.1,
                rotate: [0, -5, 5, 0],
              }}
            >
              4
            </motion.span>
            <motion.span
              className="bg-gradient-to-r from-blue-400 via-cyan-500 to-blue-500 bg-clip-text text-transparent cursor-pointer"
              animate={{
                y: [5, -5, 5],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
              whileHover={{
                scale: 1.1,
                rotate: [0, 5, -5, 0],
              }}
            >
              0
            </motion.span>
            <motion.span
              className="bg-gradient-to-r from-cyan-500 via-blue-400 to-cyan-400 bg-clip-text text-transparent cursor-pointer"
              animate={{
                y: [-5, 5, -5],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              whileHover={{
                scale: 1.1,
                rotate: [0, -5, 5, 0],
              }}
            >
              4
            </motion.span>
          </div>
        </motion.div>

        {/* Icon */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-2xl"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.05, 1],
            }}
            transition={{
              rotate: { duration: 20, repeat: Infinity, ease: "linear" },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <AlertTriangle className="w-12 h-12 text-white" />
          </motion.div>
        </motion.div>

        {/* Message */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
        >
          <motion.h2
            className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900 dark:text-white"
            animate={{
              opacity: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            Página não encontrada
          </motion.h2>

          <motion.div
            className="flex items-center justify-center gap-4 text-slate-600 dark:text-gray-400 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Zap className="size-6" />
            <span className="text-xl">
              A página foi sugada para uma dimensão paralela
            </span>
            <Sparkles className="size-6" />
          </motion.div>

          <motion.p
            className="text-lg text-slate-500 dark:text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            Mas não se preocupe! Temos um portal de volta para você.
          </motion.p>
        </motion.div>

        {/* Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={() => router.back()}
              variant="outline"
              size="lg"
              className="gap-3 bg-white/10 dark:bg-slate-800/50 border-white/20 dark:border-slate-600/50 text-slate-900 dark:text-white hover:bg-white/20 dark:hover:bg-slate-700/50 hover:border-white/30 dark:hover:border-slate-500/50 backdrop-blur-sm transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar
            </Button>
          </motion.div>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              asChild
              size="lg"
              className="gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg hover:shadow-cyan-500/25 transition-all"
            >
              <Link href="/">
                <Home className="w-5 h-5" />
                Página Inicial
              </Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Refresh Button */}
        <motion.button
          onClick={() => window.location.reload()}
          className="p-3 rounded-full bg-white/10 dark:bg-slate-800/50 hover:bg-white/20 dark:hover:bg-slate-700/50 border border-white/20 dark:border-slate-600/50 hover:border-white/30 dark:hover:border-slate-500/50 transition-all backdrop-blur-sm"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.5, ease: "easeOut" }}
          whileHover={{ scale: 1.1, rotate: 180 }}
          whileTap={{ scale: 0.9 }}
        >
          <RefreshCw className="w-5 h-5 text-slate-600 dark:text-gray-400" />
        </motion.button>
      </div>
    </div>
  );
}
